import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { ThemeContext } from '../ThemeContext';
import api from '../api';
import UserPool from '../UserPool';
import { clearAuthStorage } from '../authStorage';
import { CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { AlertTriangle, Eye, EyeOff, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    changeIdentityPlatformPassword,
    signOutIdentityPlatform,
    updateIdentityPlatformProfile,
    usesIdentityPlatform,
} from '../identityPlatform';

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isHovered 
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
};

const ProfilePage = () => {
    const { user, setUser } = useContext(UserContext);
    const { isDarkMode } = useContext(ThemeContext);
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [editedProfile, setEditedProfile] = useState(null);
    // const [isEditing, setIsEditing] = useState(false);
    const [mode, setMode] = useState('default'); // 'default' | 'edit' | 'reset'
    const [passwords, setPasswords] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [hasChanged, setHasChanged] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const isResetReady =
        passwords.current_password.trim() &&
        passwords.new_password.trim() &&
        passwords.confirm_password.trim();


    //show password states
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);


    useEffect(() => {
        if (user && user.user_id) {
            api.get(`/api/user/get_user_profile/`)
                .then(response => {
                    const profileData = response.data;
                    setProfile(profileData);
                    setEditedProfile(profileData);
                })
                .catch(error => console.error("Error fetching profile:", error));
        }
    }, [user]);

    useEffect(() => {
        if (profile && editedProfile) {
            const changes = JSON.stringify(profile) !== JSON.stringify(editedProfile);
            setHasChanged(changes);
        }
    }, [profile, editedProfile]);

    // const handleEdit = () => {
    //     setIsEditing(true);
    // };
    const handleEdit = () => setMode('edit');

    const handleCancel = () => {
        setEditedProfile(profile);
        // setIsEditing(false);
        setMode('default');
    };

    const handleResetClick = () => setMode('reset');

    const handleCancelReset = () => {
        setPasswords({ current_password: '', new_password: '', confirm_password: '' });
        setMode('default');
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswords(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordReset = async () => {
        const { current_password, new_password, confirm_password } = passwords;

        if (new_password !== confirm_password) {
            alert("New passwords do not match!");
            return;
        }

        if (usesIdentityPlatform) {
            try {
                await changeIdentityPlatformPassword(current_password, new_password);
                alert("Password reset successful!");
                setPasswords({ current_password: '', new_password: '', confirm_password: '' });
                setMode('default');
            } catch (error) {
                const message = error.code === 'auth/invalid-credential'
                    ? 'Incorrect current password. Please try again.'
                    : error.message || 'Failed to reset password.';
                alert(message);
            }
            return;
        }

        const cognitoUser = UserPool.getCurrentUser();
        if (!cognitoUser) {
            alert("Not authenticated!");
            return;
        }

        cognitoUser.getSession((err, session) => {
            if (err) {
                alert(err.message || JSON.stringify(err));
                return;
            }

            cognitoUser.changePassword(current_password, new_password, (err, result) => {
                if (err) {
                    const msg = err.message || '';
                    if (msg.includes("Incorrect username or password")) {
                        alert("Incorrect current password. Please try again.");
                    } else {
                        alert("Failed to reset password: " + msg);
                    }
                } else {
                    alert("Password reset successful!");
                    setPasswords({ current_password: '', new_password: '', confirm_password: '' });
                    setMode('default');
                }
            });
        });
    };

    const handleSave = async () => {
        if (usesIdentityPlatform) {
            try {
                await updateIdentityPlatformProfile(editedProfile.screen_name);
                const response = await api.post(
                    `/api/user/update_user_profile/`,
                    editedProfile,
                );
                const updatedProfile = response.data;
                setProfile(updatedProfile);
                setEditedProfile(updatedProfile);
                setMode('default');
                setUser({
                    ...updatedProfile,
                    firstName: updatedProfile.first_name,
                    lastName: updatedProfile.last_name,
                    screenName: updatedProfile.screen_name,
                });
                alert('Profile updated successfully!');
            } catch (error) {
                console.error('Error updating Identity Platform profile:', error);
                alert(error.message || 'Failed to update profile.');
            }
            return;
        }

        const cognitoUser = UserPool.getCurrentUser();
        if (!cognitoUser) {
            alert("Not authenticated!");
            return;
        }

        cognitoUser.getSession(async (err, session) => {
            if (err) {
                alert(err.message || JSON.stringify(err));
                return;
            }

            const attributes = [
                new CognitoUserAttribute({ Name: 'custom:FirstName', Value: editedProfile.first_name }),
                new CognitoUserAttribute({ Name: 'custom:LastName', Value: editedProfile.last_name }),
                new CognitoUserAttribute({ Name: 'custom:ScreenName', Value: editedProfile.screen_name })
            ];

            cognitoUser.updateAttributes(attributes, async (err, result) => {
                if (err) {
                    console.error("Error updating cognito attributes:", err);
                    alert(err.message || JSON.stringify(err));
                    return;
                }
                console.log('call result: ' + result);

                try {
                    // Now, update the backend database
                    const response = await api.post(`/api/user/update_user_profile/`, editedProfile);
                    const updatedProfile = response.data;

                    setProfile(updatedProfile);
                    setEditedProfile(updatedProfile);
                    // setIsEditing(false);
                    setMode('default');
                    console.log('updatedProfile', updatedProfile);
                    setUser({
                        ...updatedProfile,
                        firstName: updatedProfile.first_name,
                        lastName: updatedProfile.last_name,
                        screenName: updatedProfile.screen_name
                    });
                    alert('Profile updated successfully!');
                } catch (error) {
                    console.error("Error updating profile in backend:", error);
                    alert('Failed to update profile in backend.');
                }
            });
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedProfile(prev => ({ ...prev, [name]: value }));
    };

    const handleCloseDeleteModal = () => {
        if (isDeleting) return;
        setIsDeleteModalOpen(false);
        setDeleteConfirmation('');
        setDeleteError('');
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmation !== 'DELETE') {
            setDeleteError('Type DELETE to confirm account deletion.');
            return;
        }

        try {
            setIsDeleting(true);
            setDeleteError('');
            await api.post('/api/user/delete_account/', { confirmation: 'DELETE' });

            if (usesIdentityPlatform) {
                await signOutIdentityPlatform();
            } else {
                const cognitoUser = UserPool.getCurrentUser();
                if (cognitoUser) {
                    cognitoUser.signOut();
                }
            }

            clearAuthStorage();
            setUser(null);
            alert('Your account has been deleted.');
            navigate('/login', { replace: true });
        } catch (error) {
            const message = error.response?.data?.error || 'Failed to delete account. Please try again.';
            setDeleteError(message);
        } finally {
            setIsDeleting(false);
        }
    };

    if (!profile) {
        return <div>Loading...</div>;
    }

    const inputStyle = "w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl text-left font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-[#34c759] focus:border-[#34c759] bg-transparent dark:text-gray-100 text-gray-900";
    const buttonStyle = "py-2 sm:py-3 px-4 sm:px-8 text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full focus:outline-none focus:ring-2 focus:ring-[#34c759] focus:ring-opacity-50 disabled:bg-gray-400 dark:disabled:bg-gray-600";
    const secondaryButtonStyle = "py-2 sm:py-3 px-4 sm:px-6 text-sm sm:text-base md:text-lg font-zonaProUltra rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-500 focus:ring-opacity-50 transition-colors";
    const destructiveButtonStyle = "py-2 sm:py-3 px-4 sm:px-6 text-sm sm:text-base md:text-lg font-zonaProUltra rounded-full bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-opacity-50 transition-colors";

    return (
        <div className="min-h-screen flex flex-col items-center justify-start sm:justify-center font-zonaProRegular p-4 pb-24 pl-0 md:pl-24 overflow-x-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
            <div className="w-full max-w-2xl space-y-6 sm:space-y-8">
                <Card delay={0.2} className="p-4 sm:p-6 md:p-8">
                    <div className="mb-4 sm:mb-6">
                        <motion.h1 
                            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra"
                            style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f' }}
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            {mode === 'reset'
                                ? 'RESET PASSWORD'
                                : mode === 'edit'
                                    ? 'EDIT PROFILE'
                                    : 'MY PROFILE'}
                        </motion.h1>
                    </div>
                    <div className="space-y-4 sm:space-y-6">
                        {mode === 'reset' ? (
                            <>
                                {/* Current Password */}
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.4 }}
                                >
                                    <label className="block text-gray-700 dark:text-gray-200 text-left text-xs sm:text-sm text-zonaProRegular font-bold mb-2">Current Password</label>
                                    <div className="relative">
                                        <input
                                            type={showCurrent ? "text" : "password"}
                                            name="current_password"
                                            value={passwords.current_password}
                                            onChange={handlePasswordChange}
                                            className="w-full p-3 sm:p-4 pr-10 sm:pr-12 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-[#34c759] focus:border-[#34c759] bg-transparent dark:text-gray-100 text-gray-900"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrent(!showCurrent)}
                                            className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 bg-transparent p-0 m-0 border-none hover:bg-transparent active:bg-transparent focus:outline-none focus:ring-0"
                                        >
                                            {showCurrent ? <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" /> : <EyeOff className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />}
                                        </button>
                                    </div>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.45 }}
                                >
                                    <label className="block text-gray-700 dark:text-gray-200 text-left text-xs sm:text-sm text-zonaProRegular font-bold mb-2">New Password</label>
                                    <div className="relative">
                                        <input
                                            type={showNew ? "text" : "password"}
                                            name="new_password"
                                            value={passwords.new_password}
                                            onChange={handlePasswordChange}
                                            className="w-full p-3 sm:p-4 pr-10 sm:pr-12 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-[#34c759] focus:border-[#34c759] bg-transparent dark:text-gray-100 text-gray-900"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNew(!showNew)}
                                            className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 bg-transparent p-0 m-0 border-none hover:bg-transparent active:bg-transparent focus:outline-none focus:ring-0"
                                        >
                                            {showNew ? <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" /> : <EyeOff className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />}
                                        </button>
                                    </div>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.5 }}
                                >
                                    <label className="block text-gray-700 dark:text-gray-200 text-left text-xs sm:text-sm text-zonaProRegular font-bold mb-2">Confirm New Password</label>
                                    <div className="relative">
                                        <input
                                            type={showConfirm ? "text" : "password"}
                                            name="confirm_password"
                                            value={passwords.confirm_password}
                                            onChange={handlePasswordChange}
                                            className="w-full p-3 sm:p-4 pr-10 sm:pr-12 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-[#34c759] focus:border-[#34c759] bg-transparent dark:text-gray-100 text-gray-900"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirm(!showConfirm)}
                                            className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 bg-transparent p-0 m-0 border-none hover:bg-transparent active:bg-transparent focus:outline-none focus:ring-0"
                                        >
                                            {showConfirm ? <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" /> : <EyeOff className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />}
                                        </button>
                                    </div>
                                </motion.div>
                            </>
                        ) : (
                            <>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.4 }}
                                >
                                    <label className="block text-gray-700 dark:text-gray-200 text-left text-xs sm:text-sm text-zonaProRegular font-bold mb-2">First Name</label>
                                    {mode === 'edit' ? (
                                        <input type="text" name="first_name" value={editedProfile.first_name} onChange={handleChange} className={inputStyle} />
                                    ) : (
                                        <p className={inputStyle}>{profile.first_name}</p>
                                    )}
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.45 }}
                                >
                                    <label className="block text-gray-700 dark:text-gray-200 text-left text-xs sm:text-sm text-zonaProRegular font-bold mb-2">Last Name</label>
                                    {mode === 'edit' ? (
                                        <input type="text" name="last_name" value={editedProfile.last_name} onChange={handleChange} className={inputStyle} />
                                    ) : (
                                        <p className={inputStyle}>{profile.last_name}</p>
                                    )}
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.5 }}
                                >
                                    <label className="block text-gray-700 dark:text-gray-200 text-left text-xs sm:text-sm text-zonaProRegular font-bold mb-2">Screen Name</label>
                                    {mode === 'edit' ? (
                                        <input type="text" name="screen_name" value={editedProfile.screen_name} onChange={handleChange} className={inputStyle} />
                                    ) : (
                                        <p className={inputStyle}>{profile.screen_name}</p>
                                    )}
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.55 }}
                                >
                                    <label className="block text-gray-700 dark:text-gray-200 text-left text-xs sm:text-sm text-zonaProRegular font-bold mb-2">Email</label>
                                    <p className={`${inputStyle} bg-gray-100 dark:bg-gray-700`}>{profile.email}</p>
                                </motion.div>
                            </>
                        )}
                        <motion.div 
                            className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0 pt-4 sm:pt-6"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            {/* LEFT SIDE BUTTON: Reset Password (only in default mode) */}
                            <div className="flex items-center gap-3">
                                {mode === 'default' && (
                                    <motion.button 
                                        onClick={handleResetClick} 
                                        className={secondaryButtonStyle}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        Reset Password
                                    </motion.button>
                                )}
                            </div>

                            {/* RIGHT SIDE BUTTON GROUP */}
                            <div className="flex flex-wrap gap-2 sm:gap-4">
                                {mode === 'edit' && (
                                    <>
                                        <motion.button 
                                            onClick={handleSave} 
                                            disabled={!hasChanged} 
                                            className={buttonStyle}
                                            style={{ backgroundColor: !hasChanged ? '#9ca3af' : '#34c759' }}
                                            whileHover={hasChanged ? { scale: 1.05 } : {}}
                                            whileTap={hasChanged ? { scale: 0.95 } : {}}
                                        >
                                            Save
                                        </motion.button>
                                        <motion.button 
                                            onClick={handleCancel} 
                                            className={`${buttonStyle} bg-gradient-to-r from-red-500 to-red-600 hover:shadow-lg hover:shadow-red-500/50`}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            Cancel
                                        </motion.button>
                                    </>
                                )}
                                {mode === 'reset' && (
                                    <>
                                        <motion.button 
                                            onClick={handlePasswordReset} 
                                            disabled={!isResetReady} 
                                            className={`${buttonStyle} ${!isResetReady ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed' : 'bg-[#34c759] hover:shadow-lg hover:shadow-[#34c759]/50'}`}
                                            whileHover={isResetReady ? { scale: 1.05 } : {}}
                                            whileTap={isResetReady ? { scale: 0.95 } : {}}
                                        >
                                            Save
                                        </motion.button>
                                        <motion.button 
                                            onClick={handleCancelReset} 
                                            className={`${buttonStyle} bg-gradient-to-r from-red-500 to-red-600 hover:shadow-lg hover:shadow-red-500/50`}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            Cancel
                                        </motion.button>
                                    </>
                                )}
                                {mode === 'default' && (
                                    <motion.button 
                                        onClick={handleEdit} 
                                        className={`${buttonStyle} bg-[#34c759] hover:shadow-lg hover:shadow-[#34c759]/50`}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        Edit Profile
                                    </motion.button>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </Card>
                {mode === 'default' && (
                    <Card delay={0.35} className="p-4 sm:p-6 md:p-8 border-red-200 dark:border-red-900/60">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-start gap-3 text-left">
                                <div className="mt-1 rounded-full bg-red-100 dark:bg-red-900/40 p-2">
                                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-300" />
                                </div>
                                <div>
                                    <h2 className="text-lg sm:text-xl font-zonaProUltra text-gray-900 dark:text-gray-100">Account Deletion</h2>
                                    <p className="mt-1 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                                        Delete your account and remove access to GameYourSAT. Scores may remain as Deleted User to preserve leaderboard history.
                                    </p>
                                </div>
                            </div>
                            <motion.button
                                type="button"
                                onClick={() => setIsDeleteModalOpen(true)}
                                className={`${destructiveButtonStyle} inline-flex items-center justify-center gap-2 whitespace-nowrap`}
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                            >
                                <Trash2 className="h-4 w-4" />
                                Delete Account
                            </motion.button>
                        </div>
                    </Card>
                )}
            </div>
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        className="w-full max-w-lg rounded-2xl border border-red-200 bg-white p-6 text-left shadow-2xl dark:border-red-900/70 dark:bg-gray-900"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="delete-account-title"
                    >
                        <div className="flex items-start gap-3">
                            <div className="rounded-full bg-red-100 dark:bg-red-900/40 p-2">
                                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-300" />
                            </div>
                            <div>
                                <h2 id="delete-account-title" className="text-xl sm:text-2xl font-zonaProUltra text-gray-900 dark:text-gray-100">
                                    Delete Account
                                </h2>
                                <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                                    This will remove your login, cancel future subscription renewal when possible, and anonymize your profile. Type DELETE to confirm.
                                </p>
                            </div>
                        </div>

                        <label className="mt-5 block text-sm font-zonaProUltra text-gray-700 dark:text-gray-200">
                            Confirmation
                        </label>
                        <input
                            type="text"
                            value={deleteConfirmation}
                            onChange={(e) => {
                                setDeleteConfirmation(e.target.value);
                                setDeleteError('');
                            }}
                            className="mt-2 w-full rounded-xl border border-gray-300 bg-white p-3 text-base text-gray-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                            placeholder="Type DELETE"
                            disabled={isDeleting}
                        />

                        {deleteError && (
                            <p className="mt-3 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-200">
                                {deleteError}
                            </p>
                        )}

                        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleCloseDeleteModal}
                                disabled={isDeleting}
                                className="rounded-full border border-gray-300 px-5 py-2 text-sm sm:text-base font-zonaProUltra text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteAccount}
                                disabled={isDeleting || deleteConfirmation !== 'DELETE'}
                                className={destructiveButtonStyle}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete Account'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default ProfilePage; 
